import {
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownSection,
  DropdownTrigger,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  addToast,
} from "@heroui/react";
import { Trans } from "@lingui/react/macro";
import { t } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import * as Sentry from "@sentry/react";
import { api } from "@/lib/api-client";
import { getApiErrorMessage } from "@/utils/api-error";
import {
  ArrowLeft,
  ArrowRight,
  ChevronDown,
  ChevronRight,
  EllipsisVertical,
  ExternalLink,
  FileCheck2,
  MessageSquare,
  Plus,
} from "lucide-react";
import { InvoiceDetailsModal } from "@/pages/Dashboard/TaxRegister/Invoices/InvoiceDetailsModal";
import { TaxRegisterAuthorizationDialog } from "@/pages/Dashboard/TaxRegister/AuthorizationDialog";
import { Key, useMemo, useState } from "react";
import { LimeLoader } from "@/Components/NextBase/LimeLoader";
import { LimeModal } from "@/Components/NextBase/LimeModal";
import { LimePagination } from "@/Components/NextBase/LimePagination";
import { LimeTextarea } from "@/Components/NextBase/LimeTextarea";
import { useClientFeature } from "@/hooks/useClientFeature";
import { isBillingDocumentNotificationOn } from "./billingDocumentNotification";
import { SubscriptionInvoiceEditPeriodModal } from "./SubscriptionInvoiceEditPeriodModal";
import { SubscriptionInvoiceIssueEstimateModal } from "./SubscriptionInvoiceIssueEstimateModal";
import { SubscriptionInvoiceIssueInvoiceModal } from "./SubscriptionInvoiceIssueInvoiceModal";
import { SubscriptionInvoiceLinkDocumentModal } from "./SubscriptionInvoiceLinkDocumentModal";
import { SubscriptionInvoiceRemoveEstimateModal } from "./SubscriptionInvoiceRemoveEstimateModal";
import { SubscriptionInvoiceDeletePeriodModal } from "./SubscriptionInvoiceDeletePeriodModal";
import {
  BILLING_DOCUMENT_OFF_HINT,
  MISSING_DOCUMENT_REASON,
} from "./billingDocumentMessages";
import {
  InvoiceDocumentStatusLabel,
  InvoiceEstimateSentCell,
  InvoicePaymentCell,
  InvoicePeriodCell,
  InvoicePriceCell,
  InvoiceStatusCell,
  SharePayerCell,
} from "./SubscriptionInvoiceCells";
import {
  buildSubscriptionInvoiceRows,
  formatPeriodRange,
  getEstimateConversionUrl,
  getNextPeriodDates,
  getPeriodDocument,
  getPeriodDocuments,
  getPeriodEstimateActions,
  getPreviousPeriodDates,
  getShareCompanyName,
  getShareStatus,
  isSplitPeriod,
  type DocumentBearing,
  type PeriodDocument,
  type SubscriptionInvoice,
  type SubscriptionInvoiceRow,
  type SubscriptionInvoiceShare,
} from "./subscriptionInvoiceUtils";

/** Which payer a document action applies to. */
type PayerTarget = {
  invoiceId: number;
  shareId: number;
  payerCompanyId: number | null;
  payerName: string;
};

type RowMenuAction = {
  key:
    | "edit-period"
    | "issue-estimate"
    | "issue-invoice"
    | "link-document"
    | "remove-estimate"
    | "send-to-customer"
    | "delete";
  label: string;
  isDanger?: boolean;
  isDisabled?: boolean;
  description?: string;
  onPress: () => void;
};

type PeriodDraft = { periodStart: Date; periodEnd: Date; label: string };

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

export const SubscriptionInvoiceModal = ({
  isOpen,
  onClose,
  customerSubscriptionId,
}: {
  isOpen: boolean;
  onClose: () => void;
  customerSubscriptionId: number;
}) => {
  const authDialogRedirectUrl = (() => {
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.delete("invoiceModal");
    return currentUrl.toString();
  })();

  // Where the POS returns after converting an estimate: back here, with this table reopened.
  const periodsRedirectUrl = (() => {
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set("invoiceModal", String(customerSubscriptionId));
    return currentUrl.toString();
  })();

  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(
    null,
  );
  const [noteInvoiceId, setNoteInvoiceId] = useState<number | null>(null);
  const [invoiceNote, setInvoiceNote] = useState("");
  const [editPeriodInvoiceId, setEditPeriodInvoiceId] = useState<number | null>(
    null,
  );
  const [issueEstimateTarget, setIssueEstimateTarget] =
    useState<PayerTarget | null>(null);
  const [issueInvoiceTarget, setIssueInvoiceTarget] =
    useState<PayerTarget | null>(null);
  const [linkDocumentTarget, setLinkDocumentTarget] =
    useState<PayerTarget | null>(null);
  const [removeEstimateInvoice, setRemoveEstimateInvoice] = useState<{
    id: number;
    documentNumber: string | null;
    canceled: boolean;
    payerCompanyId: number | null;
    payerName: string;
    documentMemberCount: number;
  } | null>(null);
  // Collapsed rather than expanded ids, so co-funded periods start open.
  const [collapsedPeriodIds, setCollapsedPeriodIds] = useState<Set<number>>(
    new Set(),
  );
  const [deletePeriodInvoice, setDeletePeriodInvoice] = useState<{
    id: number;
    documents: PeriodDocument[];
  } | null>(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const {
    data: invoicesData,
    refetch: refetchInvoices,
    isPending,
  } = api.customer.useGetSubscriptionInvoices({
    customerSubscriptionId,
    query: { pagination: { page, perPage } },
  });

  const { data: allInvoicesData } = api.customer.useGetSubscriptionInvoices({
    customerSubscriptionId,
    query: { pagination: { page: 1, perPage: 100 } },
    enabled: isOpen,
  });

  const { data: currency } = api.values.useGetCurrency({});

  const { mutateAsync: updateInvoices, isPending: isUpdatingInvoice } =
    api.customer.useUpdateSubscriptionInvoice();
  const {
    mutateAsync: updateShare,
    isPending: isUpdatingShare,
    variables: updatingShare,
  } = api.customer.useUpdateSubscriptionShare();

  const { mutateAsync: createInvoice, isPending: isCreatingPeriod } =
    api.customer.useCreateSubscriptionInvoice();

  const { data: posAuthData } = api.cookie.useGetPosToken();

  const { data: subscriptionData } = api.customer.useGetCustomerSubscription({
    customerSubscriptionId,
    enabled: isOpen,
  });
  const commitmentLock = subscriptionData?.customerSubscription.commitmentLock;

  const { isFeatureEnabled } = useClientFeature();
  const { i18n } = useLingui();

  const offersBillingDocumentSend = isFeatureEnabled(
    "subscriptionNotificationSettings",
  );
  const { data: notificationSettings } =
    api.notification.useGetNotificationSettings({
      enabled: isOpen && offersBillingDocumentSend,
    });
  const canSendBillingDocument = isBillingDocumentNotificationOn({
    hasSubscriptionNotifications: offersBillingDocumentSend,
    notificationSettings,
  });
  const { mutateAsync: sendPeriodDocument } =
    api.customer.useSendSubscriptionPeriodDocument();

  const handleSendToCustomer = async (invoiceId: number) => {
    try {
      const { missingDocumentReason } = await sendPeriodDocument({
        invoiceId,
      });
      addToast({
        title: t`Obvestilo je poslano stranki`,
        description:
          missingDocumentReason === null
            ? undefined
            : t`Brez dokumenta: ${i18n._(MISSING_DOCUMENT_REASON[missingDocumentReason])}`,
        color: "success",
      });
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const handleConfirmPayment = async (shareId: number) => {
    try {
      await updateShare({ shareId, body: { paidAt: new Date() } });
      addToast({
        title: t`Uspešno potrjeno plačilo`,
        description: t`Plačilo je bilo uspešno zabeleženo`,
        color: "success",
      });
    } catch (error) {
      Sentry.captureException(error);
      addToast({
        title: t`Napaka pri potrditvi plačila`,
        description: t`Prišlo je do napake pri potrjevanju plačila. Poskusite ponovno.`,
        color: "danger",
      });
    }
  };

  const handleMarkAsUnpaid = async (shareId: number) => {
    try {
      await updateShare({ shareId, body: { paidAt: null } });
      addToast({
        title: t`Uspešno označeno kot neplačano`,
        description: t`Račun je bil označen kot neplačan`,
        color: "success",
      });
    } catch (error) {
      Sentry.captureException(error);
      addToast({
        title: t`Napaka pri označevanju kot neplačano`,
        description: t`Prišlo je do napake. Poskusite ponovno.`,
        color: "danger",
      });
    }
  };

  const periodDrafts = useMemo<
    Record<"start" | "end", PeriodDraft | null>
  >(() => {
    const subscription = subscriptionData?.customerSubscription;
    const allInvoices = allInvoicesData?.invoices ?? [];
    if (!subscription || allInvoices.length === 0) {
      return { start: null, end: null };
    }

    const { durationUnit, durationValue } = subscription;

    const newestPeriodEnd = new Date(
      Math.max(...allInvoices.map((i) => new Date(i.periodEnd).getTime())),
    );
    const oldestPeriodStart = new Date(
      Math.min(...allInvoices.map((i) => new Date(i.periodStart).getTime())),
    );

    const end = getNextPeriodDates(
      newestPeriodEnd,
      durationUnit,
      durationValue,
    );
    const start = getPreviousPeriodDates(
      oldestPeriodStart,
      durationUnit,
      durationValue,
    );

    return {
      end: { ...end, label: formatPeriodRange(end.periodStart, end.periodEnd) },
      start: {
        ...start,
        label: formatPeriodRange(start.periodStart, start.periodEnd),
      },
    };
  }, [subscriptionData, allInvoicesData]);

  const handleAddPeriod = async (position: "start" | "end") => {
    const subscription = subscriptionData?.customerSubscription;
    if (!subscription) return;

    const draft = periodDrafts[position];
    if (!draft) return;

    try {
      await createInvoice({
        customerSubscriptionId,
        // The server prices it by its place in the subscription's terms.
        body: {
          periodStart: draft.periodStart,
          periodEnd: draft.periodEnd,
        },
      });
      addToast({
        title: t`Obdobje računa je bilo uspešno ustvarjeno`,
        color: "success",
      });
    } catch (error) {
      Sentry.captureException(error);
      addToast({
        title: t`Napaka pri ustvarjanju obdobja računa`,
        description:
          getApiErrorMessage(error) ??
          t`Prišlo je do napake. Poskusite ponovno.`,
        color: "danger",
      });
    }
  };

  const handleCloseNoteModal = () => {
    setNoteInvoiceId(null);
    setInvoiceNote("");
  };

  const handleSubscriptionModalClose = () => {
    handleCloseNoteModal();
    setEditPeriodInvoiceId(null);
    setIssueEstimateTarget(null);
    setIssueInvoiceTarget(null);
    setLinkDocumentTarget(null);
    setRemoveEstimateInvoice(null);
    setDeletePeriodInvoice(null);
    onClose();
  };

  function togglePeriodExpanded(invoiceId: number) {
    setCollapsedPeriodIds((current) => {
      const next = new Set(current);
      if (!next.delete(invoiceId)) next.add(invoiceId);
      return next;
    });
  }

  const handleSaveNote = async () => {
    if (noteInvoiceId == null) {
      return;
    }

    try {
      await updateInvoices({
        invoiceId: noteInvoiceId,
        body: {
          note: invoiceNote,
        },
      });

      addToast({
        title: t`Opomba je bila uspešno shranjena`,
        color: "success",
      });

      handleCloseNoteModal();
    } catch (error) {
      Sentry.captureException(error);
      addToast({
        title: t`Napaka pri shranjevanju opombe`,
        description: t`Prišlo je do napake pri shranjevanju opombe. Poskusite ponovno.`,
        color: "danger",
      });
    }
  };

  const showTaxRegisterColumns =
    isFeatureEnabled("taxRegister") &&
    isFeatureEnabled("subscriptionsTaxRegisterIntegration");

  const columns = [
    {
      key: "period",
      label: <Trans>Obdobje</Trans>,
    },
    {
      key: "status",
      label: <Trans>Status</Trans>,
    },
    {
      key: "price",
      label: <Trans>Cena</Trans>,
    },
    ...(showTaxRegisterColumns
      ? [
          {
            key: "invoice",
            label: <Trans>Dokument</Trans>,
          },
          {
            key: "payment",
            label: <Trans>Plačilo</Trans>,
          },
          {
            key: "estimateSent",
            label: <Trans>Status e-pošte</Trans>,
          },
        ]
      : []),
    {
      key: "actions",
      label: <Trans>Akcije</Trans>,
    },
    {
      key: "note",
      label: <Trans>Opomba</Trans>,
    },
    {
      key: "menu",
      label: "",
    },
  ];

  function renderDocumentCell(document: DocumentBearing) {
    const documentId = document.spaceInvoicesDocumentId;
    if (!documentId) return null;

    return (
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <InvoiceDocumentStatusLabel document={document} />
          <Button
            size="sm"
            isIconOnly
            variant="light"
            onPress={() => setSelectedInvoiceId(documentId)}
            data-identifier="open-period-document-button"
          >
            <ExternalLink size={14} />
          </Button>
        </div>
        {document.type === "estimate" && (
          <Button
            size="sm"
            color="primary"
            variant="flat"
            startContent={<FileCheck2 size={14} />}
            data-identifier="convert-estimate-to-invoice-button"
            onPress={() => {
              window.location.href = getEstimateConversionUrl(
                documentId,
                periodsRedirectUrl,
              );
            }}
          >
            <Trans>Pretvori v račun</Trans>
          </Button>
        )}
      </div>
    );
  }

  function renderPaymentActions(share: SubscriptionInvoiceShare) {
    // Only the row being saved spins and stays disabled, so a double click cannot send it twice.
    const isSavingThisShare =
      isUpdatingShare && updatingShare?.shareId === share.id;
    return (
      <div className="flex justify-end">
        {share.paidAt == null ? (
          <Button
            size="sm"
            isLoading={isSavingThisShare}
            onPress={() => handleConfirmPayment(share.id)}
            data-identifier="confirm-payment-button"
          >
            <Trans>Potrdi plačilo</Trans>
          </Button>
        ) : (
          <Button
            size="sm"
            color="danger"
            isLoading={isSavingThisShare}
            onPress={() => handleMarkAsUnpaid(share.id)}
            data-identifier="mark-unpaid-button"
          >
            <Trans>Označi kot neplačano</Trans>
          </Button>
        )}
      </div>
    );
  }

  function getDocumentMenuActions(
    invoice: SubscriptionInvoice,
    share: SubscriptionInvoiceShare | undefined,
  ): RowMenuAction[] {
    if (!share) return [];

    // Tax-register gating only: a manual estimate is offered even when estimates are not automatic.
    const available = getPeriodEstimateActions(share, showTaxRegisterColumns);
    const payer: PayerTarget = {
      invoiceId: invoice.id,
      shareId: share.id,
      payerCompanyId: share.companyId,
      payerName: getShareCompanyName(share),
    };

    const candidates: [boolean, RowMenuAction][] = [
      [
        offersBillingDocumentSend && share.companyId === null,
        {
          key: "send-to-customer",
          label: t`Pošlji stranki`,
          ...(!canSendBillingDocument && {
            isDisabled: true,
            description: i18n._(BILLING_DOCUMENT_OFF_HINT),
          }),
          onPress: () => handleSendToCustomer(invoice.id),
        },
      ],
      [
        available.canIssueEstimate,
        {
          key: "issue-estimate",
          label: t`Izdaj predračun`,
          onPress: () => setIssueEstimateTarget(payer),
        },
      ],
      [
        available.canIssueInvoice,
        {
          key: "issue-invoice",
          label: t`Izdaj račun`,
          onPress: () => setIssueInvoiceTarget(payer),
        },
      ],
      [
        available.canLinkDocument,
        {
          key: "link-document",
          label: t`Poveži obstoječi dokument`,
          onPress: () => setLinkDocumentTarget(payer),
        },
      ],
      [
        available.canRemoveEstimate,
        {
          key: "remove-estimate",
          label: t`Odstrani predračun`,
          isDanger: true,
          onPress: () =>
            setRemoveEstimateInvoice({
              id: invoice.id,
              documentNumber: share.number ?? null,
              canceled: !!share.canceled,
              payerCompanyId: share.companyId,
              payerName: payer.payerName,
              documentMemberCount: share.documentMemberCount,
            }),
        },
      ],
    ];
    return candidates
      .filter(([isAvailable]) => isAvailable)
      .map(([, action]) => action);
  }

  function renderPeriodCell(invoice: SubscriptionInvoice, columnKey: Key) {
    const isSplit = isSplitPeriod(invoice);
    const soleShare = invoice.shares[0];
    const document = getPeriodDocument(invoice);

    switch (columnKey) {
      case "period": {
        if (!isSplit) return <InvoicePeriodCell invoice={invoice} />;

        const isExpanded = !collapsedPeriodIds.has(invoice.id);
        return (
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              isIconOnly
              variant="light"
              onPress={() => togglePeriodExpanded(invoice.id)}
              aria-label={isExpanded ? t`Skrij plačnike` : t`Prikaži plačnike`}
              data-identifier="toggle-period-payers-button"
            >
              {isExpanded ? (
                <ChevronDown size={14} />
              ) : (
                <ChevronRight size={14} />
              )}
            </Button>
            <InvoicePeriodCell invoice={invoice} />
          </div>
        );
      }
      case "status":
        return <InvoiceStatusCell invoice={invoice} />;
      case "price":
        return <InvoicePriceCell invoice={invoice} />;
      case "note": {
        const hasNote = Boolean(invoice.note?.trim());

        return (
          <Button
            size="sm"
            variant={hasNote ? "flat" : "light"}
            color={hasNote ? "primary" : "default"}
            startContent={<MessageSquare size={14} />}
            onPress={() => {
              setNoteInvoiceId(invoice.id);
              setInvoiceNote(invoice.note ?? "");
            }}
            isIconOnly={true}
            data-identifier="invoice-note-button"
          />
        );
      }
      // Every payer-scoped column belongs to the sub-rows once a period is split.
      case "invoice":
        return isSplit ? null : renderDocumentCell(document);
      case "payment":
        return isSplit ? null : (
          <InvoicePaymentCell document={document} currency={currency} />
        );
      case "estimateSent":
        return isSplit ? null : <InvoiceEstimateSentCell document={document} />;
      case "actions":
        return isSplit || !soleShare ? null : renderPaymentActions(soleShare);
      case "menu": {
        const documentActions = isSplit
          ? []
          : getDocumentMenuActions(invoice, soleShare);

        return (
          <RowActionsMenu
            ariaLabel={t`Dejanja računa`}
            actions={[
              {
                key: "edit-period",
                label: t`Uredi obdobje`,
                onPress: () => setEditPeriodInvoiceId(invoice.id),
              },
              ...documentActions,
              {
                key: "delete",
                label: t`Izbriši obdobje`,
                isDanger: true,
                ...(commitmentLock?.chainLockedPeriodIds.includes(
                  invoice.id,
                ) && {
                  isDisabled: true,
                  description: t`Obdobje je del vezave.`,
                }),
                onPress: () =>
                  setDeletePeriodInvoice({
                    id: invoice.id,
                    documents: getPeriodDocuments(invoice),
                  }),
              },
            ]}
          />
        );
      }
      default:
        return null;
    }
  }

  function renderShareCell(
    invoice: SubscriptionInvoice,
    share: SubscriptionInvoiceShare,
    isLast: boolean,
    columnKey: Key,
  ) {
    switch (columnKey) {
      case "period":
        return (
          <SharePayerCell
            share={share}
            isLast={isLast}
            dataIdentifier="share-payer-name"
          />
        );
      case "status":
        return (
          <span
            className="text-tiny text-default-500"
            data-identifier="share-status-text"
          >
            {getShareStatus(share).text}
          </span>
        );
      case "price":
        return (
          <span className="text-small" data-identifier="share-amount">
            {share.amountFormatted}
          </span>
        );
      case "invoice":
        return (
          renderDocumentCell(share) ?? (
            <span className="text-tiny text-default-400">
              <Trans>Ni dokumenta</Trans>
            </span>
          )
        );
      case "payment":
        return <InvoicePaymentCell document={share} currency={currency} />;
      case "estimateSent":
        return <InvoiceEstimateSentCell document={share} />;
      case "actions":
        return renderPaymentActions(share);
      case "menu":
        return (
          <RowActionsMenu
            ariaLabel={t`Dejanja plačnika`}
            actions={getDocumentMenuActions(invoice, share)}
          />
        );
      default:
        return null;
    }
  }

  function renderCell(row: SubscriptionInvoiceRow, columnKey: Key) {
    return row.kind === "period"
      ? renderPeriodCell(row.invoice, columnKey)
      : renderShareCell(row.invoice, row.share, row.isLast, columnKey);
  }

  const tableRows = useMemo(
    () =>
      buildSubscriptionInvoiceRows(
        (invoicesData?.invoices ?? []) as SubscriptionInvoice[],
        collapsedPeriodIds,
      ),
    [invoicesData, collapsedPeriodIds],
  );

  return (
    <>
      <LimeModal
        isOpen={isOpen}
        onClose={handleSubscriptionModalClose}
        size="5xl"
        scrollBehavior="inside"
        data-identifier="subscription-invoice-modal"
      >
        <ModalContent>
          {() => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                <Trans>Obdobja paketa</Trans>
              </ModalHeader>
              <ModalBody className="p-4 md:p-2">
                <div className="flex justify-end">
                  <AddPeriodDropdown
                    periodDrafts={periodDrafts}
                    lockedPositions={{
                      start: commitmentLock?.canPrependPeriod === false,
                      end: commitmentLock?.canAppendPeriod === false,
                    }}
                    isCreating={isCreatingPeriod}
                    onAddPeriod={handleAddPeriod}
                  />
                </div>
                {isPending && !invoicesData ? (
                  <LimeLoader />
                ) : (
                  <Table
                    aria-label="Invoices"
                    removeWrapper
                    data-identifier="subscription-invoice-table"
                  >
                    <TableHeader columns={columns}>
                      {(column) => (
                        <TableColumn key={column.key}>
                          {column.label}
                        </TableColumn>
                      )}
                    </TableHeader>
                    <TableBody items={tableRows}>
                      {(row) => (
                        <TableRow
                          key={row.key}
                          data-identifier={
                            row.kind === "period"
                              ? "subscription-invoice-row"
                              : "subscription-invoice-share-row"
                          }
                          data-invoice-id={String(row.invoice.id)}
                          data-invoice-type={
                            row.kind === "period"
                              ? row.invoice.type
                              : row.share.type
                          }
                          data-payer-company-id={
                            row.kind === "share"
                              ? String(row.share.companyId ?? "")
                              : undefined
                          }
                          data-space-document-id={
                            row.kind === "period"
                              ? row.invoice.spaceInvoicesDocumentId
                              : row.share.spaceInvoicesDocumentId
                          }
                        >
                          {(columnKey) => (
                            <TableCell>{renderCell(row, columnKey)}</TableCell>
                          )}
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                )}
                <div className="mt-2 flex w-full justify-center">
                  <LimePagination
                    paginationProps={{
                      page: page,
                      total: invoicesData?.pageCount,
                      onChange: (p) => setPage(p),
                    }}
                    perPageProps={{
                      label: t`računov`,
                      values: ROWS_PER_PAGE_OPTIONS,
                      value: String(perPage),
                      onChange: (value) => {
                        setPerPage(Number(value));
                        setPage(1);
                      },
                    }}
                  />
                </div>
              </ModalBody>
              <ModalFooter></ModalFooter>
            </>
          )}
        </ModalContent>
      </LimeModal>

      <InvoiceNoteModal
        isOpen={noteInvoiceId != null}
        note={invoiceNote}
        onNoteChange={setInvoiceNote}
        onSave={handleSaveNote}
        isSaving={isUpdatingInvoice}
        onClose={handleCloseNoteModal}
      />

      <SubscriptionInvoiceEditPeriodModal
        invoiceId={editPeriodInvoiceId}
        customerSubscriptionId={customerSubscriptionId}
        onClose={() => setEditPeriodInvoiceId(null)}
      />

      <SubscriptionInvoiceIssueEstimateModal
        shareId={issueEstimateTarget?.shareId ?? null}
        payerCompanyId={issueEstimateTarget?.payerCompanyId ?? null}
        payerName={issueEstimateTarget?.payerName ?? null}
        onClose={() => setIssueEstimateTarget(null)}
      />

      <SubscriptionInvoiceIssueInvoiceModal
        shareId={issueInvoiceTarget?.shareId ?? null}
        payerCompanyId={issueInvoiceTarget?.payerCompanyId ?? null}
        payerName={issueInvoiceTarget?.payerName ?? null}
        redirectUrl={periodsRedirectUrl}
        onClose={() => setIssueInvoiceTarget(null)}
      />

      <SubscriptionInvoiceLinkDocumentModal
        invoiceId={linkDocumentTarget?.invoiceId ?? null}
        customerId={subscriptionData?.customerSubscription?.customerId ?? null}
        payerCompanyId={linkDocumentTarget?.payerCompanyId ?? null}
        payerName={linkDocumentTarget?.payerName ?? null}
        onClose={() => setLinkDocumentTarget(null)}
      />

      <SubscriptionInvoiceRemoveEstimateModal
        invoiceId={removeEstimateInvoice?.id ?? null}
        documentNumber={removeEstimateInvoice?.documentNumber ?? null}
        canceled={removeEstimateInvoice?.canceled ?? false}
        payerCompanyId={removeEstimateInvoice?.payerCompanyId ?? null}
        payerName={removeEstimateInvoice?.payerName ?? null}
        documentMemberCount={removeEstimateInvoice?.documentMemberCount ?? 1}
        onClose={() => setRemoveEstimateInvoice(null)}
      />

      <SubscriptionInvoiceDeletePeriodModal
        invoice={deletePeriodInvoice}
        onClose={() => setDeletePeriodInvoice(null)}
      />

      <InvoiceDetailsModal
        isAuthenticated={posAuthData?.userId != null}
        invoiceId={selectedInvoiceId || undefined}
        redirectUrl={periodsRedirectUrl}
        onClose={() => {
          setSelectedInvoiceId(null);
          refetchInvoices();
        }}
        disableNavigationOnCancel
      />

      {isOpen && showTaxRegisterColumns && (
        <TaxRegisterAuthorizationDialog redirectUrl={authDialogRedirectUrl} />
      )}
    </>
  );
};

function RowActionsMenu({
  actions,
  ariaLabel,
}: {
  actions: RowMenuAction[];
  ariaLabel: string;
}) {
  if (actions.length === 0) return null;

  return (
    <div className="flex justify-end">
      <Dropdown placement="bottom-end">
        <DropdownTrigger>
          <Button
            isIconOnly
            size="sm"
            variant="light"
            startContent={<EllipsisVertical height={16} width={16} />}
            data-identifier="row-actions-menu-trigger"
          />
        </DropdownTrigger>
        <DropdownMenu aria-label={ariaLabel} items={actions}>
          {(action) => (
            <DropdownItem
              key={action.key}
              color={action.isDanger ? "danger" : undefined}
              className={action.isDanger ? "text-danger" : undefined}
              isDisabled={action.isDisabled}
              description={action.description}
              onPress={action.onPress}
              data-identifier={`${action.key}-button`}
            >
              {action.label}
            </DropdownItem>
          )}
        </DropdownMenu>
      </Dropdown>
    </div>
  );
}

function AddPeriodDropdown({
  periodDrafts,
  lockedPositions,
  isCreating,
  onAddPeriod,
}: {
  periodDrafts: Record<"start" | "end", PeriodDraft | null>;
  /** Where a commitment's positions would be renumbered by a new period. */
  lockedPositions: Record<"start" | "end", boolean>;
  isCreating: boolean;
  onAddPeriod: (position: "start" | "end") => void;
}) {
  const positions = [
    {
      position: "end",
      label: t`Na koncu`,
      icon: <ArrowRight size={18} />,
      iconClass: "bg-primary-100 text-primary-600",
      showDivider: true,
    },
    {
      position: "start",
      label: t`Na začetku`,
      icon: <ArrowLeft size={18} />,
      iconClass: "bg-default-100 text-default-500",
      showDivider: false,
    },
  ] as const;

  return (
    <Dropdown placement="bottom-end">
      <DropdownTrigger>
        <Button
          color="primary"
          isLoading={isCreating}
          startContent={<Plus size={18} />}
          data-identifier="add-period-button"
          size="sm"
        >
          <Trans>Dodaj obdobje</Trans>
        </Button>
      </DropdownTrigger>
      <DropdownMenu aria-label={t`Dodaj obdobje`} variant="flat">
        <DropdownSection
          title={t`Novo obdobje`}
          classNames={{ heading: "uppercase text-foreground-400" }}
        >
          {positions.map(
            ({ position, label, icon, iconClass, showDivider }) => (
              <DropdownItem
                key={position}
                showDivider={showDivider}
                isDisabled={
                  !periodDrafts[position] || lockedPositions[position]
                }
                onPress={() => onAddPeriod(position)}
                textValue={label}
                description={
                  lockedPositions[position]
                    ? t`Tu bi novo obdobje premaknilo vezavo.`
                    : periodDrafts[position]?.label
                }
                startContent={
                  <div
                    className={`${iconClass} flex h-9 w-9 shrink-0 items-center justify-center rounded-xl`}
                  >
                    {icon}
                  </div>
                }
                classNames={{ title: "font-medium" }}
                data-identifier={`add-period-${position}-button`}
              >
                {label}
              </DropdownItem>
            ),
          )}
        </DropdownSection>
      </DropdownMenu>
    </Dropdown>
  );
}

function InvoiceNoteModal({
  isOpen,
  note,
  onNoteChange,
  onSave,
  isSaving,
  onClose,
}: {
  isOpen: boolean;
  note: string;
  onNoteChange: (note: string) => void;
  onSave: () => void;
  isSaving: boolean;
  onClose: () => void;
}) {
  return (
    <LimeModal
      isOpen={isOpen}
      onClose={onClose}
      data-identifier="subscription-invoice-note-modal"
    >
      <ModalContent>
        <ModalHeader>
          <Trans>Opomba</Trans>
        </ModalHeader>
        <ModalBody>
          <LimeTextarea
            value={note}
            onValueChange={onNoteChange}
            variant="bordered"
            minRows={5}
            data-identifier="subscription-invoice-note-textarea"
          />
        </ModalBody>
        <ModalFooter>
          <Button variant="light" onPress={onClose}>
            <Trans>Prekliči</Trans>
          </Button>
          <Button
            color="primary"
            onPress={onSave}
            isLoading={isSaving}
            data-identifier="subscription-invoice-note-save-button"
          >
            <Trans>Shrani</Trans>
          </Button>
        </ModalFooter>
      </ModalContent>
    </LimeModal>
  );
}
